/**
 * WellDetailsModal
 * 
 * Modal dialog for well details, replacing the bottom drawer.
 * Shows well info, route preview, navigation options.
 * Styled like NearbyServicesWidget with glassmorphism.
 */

import { useState, useEffect } from 'react';
import {
  Box,
  Button,
  Chip,
  Dialog,
  DialogContent,
  DialogTitle,
  IconButton,
  Typography,
  Collapse,
  Tooltip,
  Fab,
} from '@mui/material';
import {
  Close as CloseIcon,
  ContentCopy as ContentCopyIcon,
  // Route as RouteIcon, // Removed - route button removed
  // PhotoCamera as PhotoCameraIcon, // Removed - street view button removed  
  Google as GoogleIcon,
  Route as RouteIcon,
  ThumbUp as ThumbUpIcon,
} from '@mui/icons-material';
import ZiaCompassIcon from './icons/ZiaCompassIcon';
import { Well } from '../types';
import { getCyberColors } from '../theme/cyberColors';
import { RoutePhotosGallery } from './RoutePhotosGallery';
import { ProductionChart } from './ProductionChart';
import RouteService, { FacilityRoute } from '../services/routeService';
import { telegram } from '../services/telegram';

// Get status color based on well status
const getStatusColors = (status: string | undefined): { bg: string; text: string; border: string } => {
  const s = (status || '').toLowerCase();
  
  // Active/Producing states - Green
  if (s.includes('active') || s.includes('producing') || s.includes('drilling') || s.includes('completed')) {
    return { bg: 'rgba(16, 185, 129, 0.2)', text: '#10b981', border: '#10b981' };
  }
  // New/Permit states - Cyan
  if (s.includes('new') || s.includes('permit') || s.includes('approved') || s.includes('pending')) {
    return { bg: 'rgba(6, 182, 212, 0.2)', text: '#06b6d4', border: '#06b6d4' };
  }
  // Inactive/Shut-in states - Amber
  if (s.includes('inactive') || s.includes('shut') || s.includes('suspended') || s.includes('idle')) {
    return { bg: 'rgba(245, 158, 11, 0.2)', text: '#f59e0b', border: '#f59e0b' };
  }
  // Plugged/Abandoned states - Red
  if (s.includes('plugged') || s.includes('abandoned') || s.includes('p&a') || s.includes('ta')) {
    return { bg: 'rgba(239, 68, 68, 0.2)', text: '#ef4444', border: '#ef4444' };
  }
  // Cancelled/Denied states - Purple
  if (s.includes('cancelled') || s.includes('denied') || s.includes('expired') || s.includes('void')) {
    return { bg: 'rgba(168, 85, 247, 0.2)', text: '#a855f7', border: '#a855f7' };
  }
  // Default - Gray
  return { bg: 'rgba(148, 163, 184, 0.2)', text: '#94a3b8', border: '#94a3b8' };
};

// Vehicle type definitions
type VehicleType = 'dot' | 'tractor' | 'semi' | 'welding' | 'pickup' | 'company' | 'vacuum' | 'rig' | 'car' | 'safety' | 'water' | 'hotOiler' | 'oilTruck' | 'monster' | 'electric' | 'vapor' | 'crane' | 'sandTank' | 'raceCar' | 'motorcycle' | 'eagle' | 'fracking' | 'drilling' | 'roadrunner';

interface RouteInfo {
  distance: string;
  duration: string;
  summary: string;
}

interface WellDetailsModalProps {
  well: Well | null;
  open: boolean;
  onClose: () => void;
  darkMode?: boolean;
  onShowRoute: (well: Well) => Promise<void>;
  onNavigate: () => void;
  onStreetView: (lat: number, lng: number, title: string) => void;
  onClearRoute: () => void;
  routeInfo: RouteInfo | null;
  isLoadingRoute: boolean;
  navigationSteps: any[];
  landmarkWaypoints?: Array<{ lat: number; lng: number }>;
  userVehicle: VehicleType;
  onVehicleChange: (vehicle: VehicleType) => void;
  onCopyApi: (api: string) => void;
  onShowCommunityRoute?: (route: FacilityRoute) => void;
}

export function WellDetailsModal({
  well,
  open,
  onClose,
  darkMode = true,
  onShowRoute,
  onNavigate,
  onStreetView: _onStreetView, // eslint-disable-line @typescript-eslint/no-unused-vars
  onClearRoute,
  routeInfo,
  isLoadingRoute: _isLoadingRoute, // eslint-disable-line @typescript-eslint/no-unused-vars
  navigationSteps,
  landmarkWaypoints,
  userVehicle: _userVehicle, // eslint-disable-line @typescript-eslint/no-unused-vars
  onVehicleChange: _onVehicleChange, // eslint-disable-line @typescript-eslint/no-unused-vars
  onCopyApi,
  onShowCommunityRoute,
}: WellDetailsModalProps) {
  const cyber = getCyberColors(darkMode);
  const [calicheRoutes, setCalicheRoutes] = useState<FacilityRoute[]>([]);
  const [routesExpanded, setRoutesExpanded] = useState(false);
  
  // Fetch caliche routes when well changes
  useEffect(() => {
    if (open && well?.api) {
      const userId = telegram.getUserId();
      RouteService.getRoutes(well.api, 'well', userId || undefined)
        .then((result) => {
          if (result.success && result.routes) {
            setCalicheRoutes(result.routes);
          } else {
            setCalicheRoutes([]);
          }
        })
        .catch(() => setCalicheRoutes([]));
    } else {
      setCalicheRoutes([]);
    }
  }, [open, well?.api]);
  
  if (!well) return null;
  
  const statusColors = getStatusColors(well.status);

  // Keep onShowRoute prop used for route calculation
  void onShowRoute;

  return (
    <Dialog
      open={open}
      onClose={onClose}
      maxWidth="md"
      fullWidth
      PaperProps={{
        sx: {
          backgroundColor: darkMode ? 'rgba(17, 24, 39, 0.98)' : 'rgba(255, 255, 255, 0.98)',
          backdropFilter: 'blur(20px)',
          borderRadius: 3,
          border: `1px solid ${darkMode ? 'rgba(0, 255, 255, 0.3)' : 'rgba(0, 0, 0, 0.1)'}`,
          maxHeight: '85vh',
          margin: 2,
        }
      }}
    >
      {/* Header */}
      <DialogTitle sx={{ 
        display: 'flex', 
        alignItems: 'center', 
        justifyContent: 'space-between',
        borderBottom: `1px solid ${darkMode ? 'rgba(255,255,255,0.1)' : 'rgba(0,0,0,0.1)'}`,
        pb: 2,
        pt: 2,
      }}>
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 2, flex: 1, minWidth: 0 }}>
          {/* Status Color Indicator */}
          <Box sx={{
            width: 12,
            height: 12,
            borderRadius: '50%',
            backgroundColor: statusColors.text,
            boxShadow: darkMode ? `0 0 10px ${statusColors.text}` : `0 0 6px ${statusColors.text}80`,
            flexShrink: 0,
          }} />
          
          {/* Well Name & Type */}
          <Box sx={{ minWidth: 0, flex: 1 }}>
            <Typography 
              variant="h5" 
              sx={{ 
                color: darkMode ? cyber.neonBlue : cyber.neonBlue, 
                fontWeight: 700,
                textShadow: darkMode ? `0 0 12px ${cyber.neonBlue}80` : 'none',
                overflow: 'hidden',
                textOverflow: 'ellipsis',
                whiteSpace: 'nowrap',
              }}
            >
              {well.wellName || 'Unknown Well'}
            </Typography>
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mt: 0.5 }}>
              {/* Type Badge */}
              <Box sx={{
                display: 'inline-flex',
                alignItems: 'center',
                gap: 0.5,
                px: 1,
                py: 0.25,
                borderRadius: 1,
                background: darkMode ? `${cyber.neonPurple}25` : `${cyber.neonPurple}15`,
                border: darkMode ? `1px solid ${cyber.neonPurple}50` : `1px solid ${cyber.neonPurple}40`,
              }}>
                <Typography sx={{
                  fontSize: '0.65rem',
                  fontWeight: 700,
                  color: cyber.neonPurple,
                  textTransform: 'uppercase',
                  letterSpacing: 1,
                }}>
                  {well.type || well.wellType || 'WELL'}
                </Typography>
              </Box>
              {/* Status Badge */}
              <Box sx={{
                px: 1,
                py: 0.25,
                borderRadius: 1,
                background: statusColors.bg,
                border: `1px solid ${statusColors.border}`,
              }}>
                <Typography sx={{ 
                  color: statusColors.text,
                  fontSize: '0.65rem',
                  fontWeight: 700,
                  textTransform: 'uppercase',
                }}>
                  {well.status || 'Unknown'}
                </Typography>
              </Box>
              {/* Caliche Routes Badge */}
              {calicheRoutes.length > 0 && (
                <Box 
                  sx={{
                    px: 1,
                    py: 0.25,
                    borderRadius: 1,
                    background: `${cyber.neonOrange}25`,
                    border: `1px solid ${cyber.neonOrange}`,
                    cursor: 'pointer',
                    display: 'flex',
                    alignItems: 'center',
                    gap: 0.5,
                    '&:hover': {
                      background: `${cyber.neonOrange}35`,
                    }
                  }}
                  onClick={() => setRoutesExpanded(!routesExpanded)}
                >
                  <RouteIcon sx={{ fontSize: '0.7rem', color: cyber.neonOrange }} />
                  <Typography sx={{ 
                    color: cyber.neonOrange,
                    fontSize: '0.65rem',
                    fontWeight: 700,
                    textTransform: 'uppercase',
                  }}>
                    {calicheRoutes.length} Route{calicheRoutes.length > 1 ? 's' : ''}
                  </Typography>
                </Box>
              )}
            </Box>
          </Box>
        </Box>
        
        <IconButton onClick={onClose} sx={{ color: darkMode ? '#9ca3af' : '#666', ml: 1 }}>
          <CloseIcon />
        </IconButton>
      </DialogTitle>

      <DialogContent sx={{ p: 0 }}>
        {/* Route Preview (if available) */}
        {routeInfo && (
          <Box sx={{ 
            mx: 2.5,
            mt: 2,
            p: 2,
            borderRadius: 2,
            background: `linear-gradient(135deg, ${cyber.neonCyan}15 0%, ${cyber.neonGreen}10 100%)`,
            border: `2px solid ${cyber.neonCyan}60`,
            boxShadow: darkMode ? `0 0 20px ${cyber.neonCyan}30` : 'none',
          }}>
            <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 1 }}>
              <Typography sx={{ 
                color: cyber.neonCyan, 
                fontWeight: 700, 
                fontSize: '0.85rem',
                textTransform: 'uppercase',
                letterSpacing: 1.5,
                textShadow: darkMode ? `0 0 10px ${cyber.neonCyan}` : 'none'
              }}>
                📍 Route Preview
              </Typography>
              <IconButton 
                size="small" 
                onClick={onClearRoute}
                sx={{ 
                  color: cyber.textMuted,
                  '&:hover': { color: cyber.neonRed }
                }}
              >
                <CloseIcon sx={{ fontSize: 18 }} />
              </IconButton>
            </Box>
            <Box sx={{ display: 'flex', gap: 3, flexWrap: 'wrap' }}>
              <Box>
                <Typography sx={{ color: cyber.textMuted, fontSize: '0.7rem', textTransform: 'uppercase' }}>
                  Distance
                </Typography>
                <Typography sx={{ 
                  color: cyber.neonGreen, 
                  fontWeight: 700, 
                  fontSize: '1.2rem',
                  fontFamily: 'monospace',
                  textShadow: darkMode ? `0 0 8px ${cyber.neonGreen}` : 'none'
                }}>
                  {routeInfo.distance}
                </Typography>
              </Box>
              <Box>
                <Typography sx={{ color: cyber.textMuted, fontSize: '0.7rem', textTransform: 'uppercase' }}>
                  Est. Time
                </Typography>
                <Typography sx={{ 
                  color: cyber.neonBlue, 
                  fontWeight: 700, 
                  fontSize: '1.2rem',
                  fontFamily: 'monospace',
                  textShadow: darkMode ? `0 0 8px ${cyber.neonBlue}` : 'none'
                }}>
                  {routeInfo.duration}
                </Typography>
              </Box>
            </Box>
            <Typography sx={{ 
              color: cyber.textSecondary, 
              fontSize: '0.75rem', 
              mt: 1,
              fontStyle: 'italic'
            }}>
              via {routeInfo.summary}
            </Typography>
          </Box>
        )}

        {/* Caliche Routes Section */}
        <Collapse in={routesExpanded && calicheRoutes.length > 0}>
          <Box sx={{ 
            mx: 2.5,
            mt: 2,
            p: 2,
            borderRadius: 2,
            background: `linear-gradient(135deg, ${cyber.neonOrange}15 0%, ${cyber.neonYellow}10 100%)`,
            border: `1px solid ${cyber.neonOrange}50`,
            boxShadow: darkMode ? `0 0 15px ${cyber.neonOrange}20` : 'none',
          }}>
            <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', mb: 1.5 }}>
              <Typography sx={{ 
                color: cyber.neonOrange, 
                fontWeight: 700, 
                fontSize: '0.85rem',
                textTransform: 'uppercase',
                letterSpacing: 1,
                display: 'flex',
                alignItems: 'center',
                gap: 0.5,
              }}>
                🛣️ Caliche Routes
              </Typography>
              <IconButton 
                size="small" 
                onClick={() => setRoutesExpanded(false)}
                sx={{ color: cyber.textMuted }}
              >
                <CloseIcon sx={{ fontSize: 16 }} />
              </IconButton>
            </Box>
            
            {calicheRoutes.map((route, idx) => (
              <Box 
                key={route.id}
                sx={{
                  p: 1.5,
                  mb: idx < calicheRoutes.length - 1 ? 1 : 0,
                  borderRadius: 1.5,
                  background: darkMode ? 'rgba(0,0,0,0.3)' : 'rgba(255,255,255,0.5)',
                  border: `1px solid ${cyber.borderDefault}`,
                  cursor: onShowCommunityRoute ? 'pointer' : 'default',
                  transition: 'all 0.2s',
                  '&:hover': onShowCommunityRoute ? {
                    borderColor: cyber.neonOrange,
                    transform: 'translateX(4px)',
                  } : {},
                }}
                onClick={() => onShowCommunityRoute?.(route)}
              >
                <Typography sx={{ 
                  color: cyber.textPrimary, 
                  fontWeight: 600, 
                  fontSize: '0.85rem',
                  mb: 0.5,
                }}>
                  {route.description || `Route to ${well.wellName}`}
                </Typography>
                
                {route.startDescription && (
                  <Typography sx={{ 
                    color: cyber.textSecondary, 
                    fontSize: '0.75rem',
                    mb: 0.5,
                  }}>
                    From: {route.startDescription}
                  </Typography>
                )}
                
                <Box sx={{ display: 'flex', gap: 1, flexWrap: 'wrap', alignItems: 'center' }}>
                  {route.waypointCount > 0 && (
                    <Chip
                      size="small"
                      label={`${route.waypointCount} waypoints`}
                      sx={{
                        height: 20,
                        fontSize: '0.65rem',
                        bgcolor: `${cyber.neonCyan}20`,
                        color: cyber.neonCyan,
                      }}
                    />
                  )}
                  <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5, ml: 'auto' }}>
                    <ThumbUpIcon sx={{ fontSize: 12, color: route.score > 0 ? '#22c55e' : cyber.textMuted }} />
                    <Typography sx={{ 
                      fontSize: '0.7rem', 
                      fontWeight: 600,
                      color: route.score > 0 ? '#22c55e' : route.score < 0 ? '#ef4444' : cyber.textMuted,
                    }}>
                      {route.score}
                    </Typography>
                  </Box>
                </Box>
              </Box>
            ))}
          </Box>
        </Collapse>

        {/* Well Details Grid */}
        <Box sx={{ 
          display: 'flex', 
          flexDirection: 'column', 
          gap: 2, 
          m: 2.5,
          p: 2.5,
          borderRadius: 2,
          background: darkMode ? cyber.bgGlass : 'rgba(0,0,0,0.03)',
          border: darkMode ? `1px solid ${cyber.borderDefault}` : '1px solid rgba(0,0,0,0.08)',
        }}>
          {/* API */}
          <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
            <Typography sx={{ 
              color: darkMode ? cyber.textMuted : '#666', 
              fontSize: '0.9rem', 
              fontWeight: 600,
              textTransform: 'uppercase',
              letterSpacing: 1
            }}>
              API
            </Typography>
            <Chip
              label={well.api || 'N/A'}
              size="small"
              icon={<ContentCopyIcon sx={{ fontSize: '0.9rem !important' }} />}
              onClick={() => well.api && onCopyApi(well.api)}
              sx={{
                height: 28,
                fontSize: '0.85rem',
                fontWeight: 700,
                fontFamily: '"Courier New", monospace',
                backgroundColor: darkMode ? `${cyber.neonGreen}25` : `${cyber.neonGreen}15`,
                color: cyber.neonGreen,
                cursor: 'pointer',
                textShadow: darkMode ? `0 0 8px ${cyber.neonGreen}60` : 'none',
                '& .MuiChip-icon': { color: cyber.neonGreen },
                '&:hover': {
                  backgroundColor: darkMode ? `${cyber.neonGreen}40` : `${cyber.neonGreen}25`,
                }
              }}
            />
          </Box>

          {/* Operator */}
          <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
            <Typography sx={{ 
              color: darkMode ? cyber.textMuted : '#666', 
              fontSize: '0.9rem', 
              fontWeight: 600,
              textTransform: 'uppercase',
              letterSpacing: 1
            }}>
              OPERATOR
            </Typography>
            <Typography sx={{ 
              color: darkMode ? cyber.textPrimary : '#111', 
              textAlign: 'right', 
              maxWidth: '60%', 
              fontSize: '0.9rem',
              fontWeight: 600
            }}>
              {well.operator || 'Unknown'}
            </Typography>
          </Box>

          {/* County */}
          <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
            <Typography sx={{ 
              color: darkMode ? cyber.textMuted : '#666', 
              fontSize: '0.9rem', 
              fontWeight: 600,
              textTransform: 'uppercase',
              letterSpacing: 1
            }}>
              COUNTY
            </Typography>
            <Typography sx={{ 
              color: darkMode ? cyber.textPrimary : '#111', 
              fontSize: '0.9rem',
              fontWeight: 600
            }}>
              {well.county || 'Unknown'}
            </Typography>
          </Box>

          {/* Basin */}
          {well.basin && (
            <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
              <Typography sx={{ 
                color: darkMode ? cyber.textMuted : '#666', 
                fontSize: '0.9rem', 
                fontWeight: 600,
                textTransform: 'uppercase',
                letterSpacing: 1
              }}>
                BASIN
              </Typography>
              <Typography sx={{ 
                color: darkMode ? cyber.textPrimary : '#111', 
                fontSize: '0.9rem',
                fontWeight: 600
              }}>
                {well.basin}
              </Typography>
            </Box>
          )}

          {/* Coordinates */}
          <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
            <Typography sx={{ 
              color: darkMode ? cyber.textMuted : '#666', 
              fontSize: '0.9rem', 
              fontWeight: 600,
              textTransform: 'uppercase',
              letterSpacing: 1
            }}>
              COORDS
            </Typography>
            <Box sx={{ display: 'flex', gap: 1 }}>
              <Box 
                onClick={() => window.open(`https://www.google.com/maps/search/?api=1&query=${well.latitude},${well.longitude}`, '_blank')}
                sx={{
                  display: 'flex',
                  alignItems: 'center',
                  gap: 0.5,
                  backgroundColor: `${cyber.neonGreen}20`,
                  border: `1px solid ${cyber.neonGreen}60`,
                  borderRadius: '6px',
                  px: 1,
                  py: 0.25,
                  cursor: 'pointer',
                  transition: 'all 0.2s',
                  '&:hover': { backgroundColor: `${cyber.neonGreen}40`, transform: 'scale(1.02)' }
                }}
              >
                <Typography sx={{ color: cyber.neonGreen, fontSize: '0.6rem', fontWeight: 700, opacity: 0.8 }}>LAT</Typography>
                <Typography sx={{ 
                  color: cyber.neonGreen, 
                  fontSize: '0.75rem', 
                  fontFamily: 'monospace', 
                  fontWeight: 700,
                  textShadow: darkMode ? `0 0 6px ${cyber.neonGreen}60` : 'none'
                }}>
                  {well.latitude?.toFixed(5)}
                </Typography>
              </Box>
              <Box 
                onClick={() => window.open(`https://www.google.com/maps/search/?api=1&query=${well.latitude},${well.longitude}`, '_blank')}
                sx={{
                  display: 'flex',
                  alignItems: 'center',
                  gap: 0.5,
                  backgroundColor: `${cyber.neonYellow}20`,
                  border: `1px solid ${cyber.neonYellow}60`,
                  borderRadius: '6px',
                  px: 1,
                  py: 0.25,
                  cursor: 'pointer',
                  transition: 'all 0.2s',
                  '&:hover': { backgroundColor: `${cyber.neonYellow}40`, transform: 'scale(1.02)' }
                }}
              >
                <Typography sx={{ color: cyber.neonYellow, fontSize: '0.6rem', fontWeight: 700, opacity: 0.8 }}>LNG</Typography>
                <Typography sx={{ 
                  color: cyber.neonYellow, 
                  fontSize: '0.75rem', 
                  fontFamily: 'monospace', 
                  fontWeight: 700,
                  textShadow: darkMode ? `0 0 6px ${cyber.neonYellow}60` : 'none'
                }}>
                  {well.longitude?.toFixed(5)}
                </Typography>
              </Box>
            </Box>
          </Box>
        </Box>

        {/* Google Maps Button (oval) */}
        <Box
          sx={{
            display: 'flex',
            width: '100%',
            px: 2.5,
            mt: 0.5,
            mb: 2,
          }}
        >
          <Tooltip title="Google Maps" arrow>
            <Fab
              variant="extended"
              color="primary"
              onClick={() => {
                telegram.hapticFeedback('light');
                const waypointParam = (landmarkWaypoints || [])
                  .filter(wp => typeof wp.lat === 'number' && typeof wp.lng === 'number')
                  .map(wp => `${wp.lat},${wp.lng}`)
                  .slice(0, 23); // Google supports up to 23 waypoints
                const waypointStr = waypointParam.length > 0 
                  ? `&waypoints=${encodeURIComponent(waypointParam.join('|'))}`
                  : '';
                window.open(
                  `https://www.google.com/maps/dir/?api=1&destination=${well.latitude},${well.longitude}&travelmode=driving${waypointStr}`,
                  '_blank'
                );
              }}
              aria-label="Google Maps"
              sx={{
                height: 72,
                width: '100%',
                flex: 1,
                px: 4,
                borderRadius: 999,
                backgroundColor: '#4285F4',
                color: '#fff',
                fontWeight: 700,
                letterSpacing: 0.5,
                gap: 1,
                boxShadow: darkMode
                  ? '0 12px 30px rgba(66, 133, 244, 0.5)'
                  : '0 12px 26px rgba(66, 133, 244, 0.45)',
                '&:hover': {
                  backgroundColor: '#2f7ae5',
                  boxShadow: darkMode
                    ? '0 14px 34px rgba(66, 133, 244, 0.6)'
                    : '0 14px 30px rgba(66, 133, 244, 0.52)',
                },
              }}
            >
              <GoogleIcon sx={{ fontSize: '2rem' }} />
              Google Maps
            </Fab>
          </Tooltip>
        </Box>

        {/* Route Photos Section */}
        <Box sx={{ 
          mx: 2.5,
          mb: 2.5,
          borderRadius: 2,
          background: darkMode ? cyber.bgGlass : 'rgba(0,0,0,0.02)',
          border: `1px solid ${cyber.neonOrange}30`,
          overflow: 'hidden',
        }}>
          <RoutePhotosGallery
            facilityId={well.api || well.id}
            facilityType={(well as any).type || 'well'}
            facilityLat={well.latitude}
            facilityLng={well.longitude}
            compact={false}
          />
        </Box>

        {/* Production Chart Section - Only show for wells/SWDs */}
        {(well.type === 'well' || well.wellType === 'OIL' || well.wellType === 'GAS' || 
          well.type === 'swd' || (well as any).type?.toLowerCase().includes('well') ||
          !well.type) && well.api && (
          <Box sx={{ 
            mx: 2.5,
            mb: 2.5,
            p: 2,
            borderRadius: 2,
            background: darkMode ? cyber.bgGlass : 'rgba(0,0,0,0.02)',
            border: `1px solid ${cyber.neonCyan}30`,
          }}>
            <ProductionChart 
              wellId={well.api}
              darkMode={darkMode}
              monthsBack={24}
              height={250}
            />
          </Box>
        )}

        {/* Start Navigation Button (when route is ready) */}
        {routeInfo && navigationSteps.length > 0 && (
          <Box sx={{ px: 2.5, pb: 2.5 }}>
            <Button
              variant="contained"
              fullWidth
              onClick={onNavigate}
              startIcon={<ZiaCompassIcon />}
              sx={{
                py: 1.5,
                fontSize: '0.9rem',
                fontWeight: 700,
                letterSpacing: 1,
                textTransform: 'uppercase',
                borderRadius: 2,
                background: `linear-gradient(135deg, ${cyber.neonGreen} 0%, ${cyber.neonCyan} 100%)`,
                color: '#000',
                boxShadow: darkMode ? `0 4px 20px ${cyber.neonGreen}40` : `0 4px 12px ${cyber.neonGreen}30`,
                '&:hover': {
                  background: `linear-gradient(135deg, ${cyber.neonCyan} 0%, ${cyber.neonGreen} 100%)`,
                  boxShadow: darkMode ? `0 6px 30px ${cyber.neonGreen}60` : `0 6px 20px ${cyber.neonGreen}40`,
                }
              }}
            >
              Start In-App Navigation
            </Button>
          </Box>
        )}
      </DialogContent>
    </Dialog>
  );
}
